import {
  Center,
  Group,
  Loader,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldMapsLayout } from '../../../../components/field-maps-layout/field-maps-layout';
import SelectMapType from '../../../../components/select-map-type/select-map-type';
import useSharedFieldMaps from '../../../../hooks/use-shared-field-maps/shared-field-maps';
import { MAP_DISPLAY_TYPES } from '../compare-maps/page';

import classes from './page.module.css';

import { FORM_STATUS, layersOptions } from '@data-access';
import {
  GradientLegend,
  LegendControl,
  PfMap,
  PlotsLayer,
  SelectImageSlider,
  useWmsLayerInfo,
  WmsLayer,
} from '@map';

interface SatelliteMapProps {
  type: MAP_DISPLAY_TYPES;
}

export function SatelliteMap({ type }: SatelliteMapProps) {
  const { t } = useTranslation();
  const {
    field,
    compare,
    fieldId,
    queries,
    handleMapLoad,
    handleCompareChange,
  } = useSharedFieldMaps({ type });

  const [heatmapType, setHeatmapType] = useState('NDVI');
  const [selectedHeatmap, setSelectedHeatmap] = useState<string | null>(null);

  const maps = useQuery({
    ...queries.heatmapsSummary({
      fieldId: Number(fieldId),
      heatmapType: heatmapType,
    }),
    refetchInterval(query) {
      return query.state.data?.status !== FORM_STATUS.FINISHED ? 5000 : false;
    },
  });

  const selectedMap = maps.data?.data.find(
    (map) => map.id?.toString() === selectedHeatmap,
  );

  const { handleWmsLayerClick, wmsInfoPopup } = useWmsLayerInfo({
    imageryIndex: heatmapType,
    wmsSrc: selectedMap?.url,
  });

  useEffect(() => {
    setSelectedHeatmap(null);
  }, [heatmapType]);

  useEffect(() => {
    if (maps.data?.options?.length && !selectedHeatmap) {
      setSelectedHeatmap(maps.data.options[0].value);
    }
  }, [maps.data, selectedHeatmap]);

  const renderMapContent = () => {
    if (maps.data?.status === FORM_STATUS.FINISHED && selectedMap) {
      return (
        <Stack gap={0} h="100%">
          <PfMap
            classnames={{
              container: clsx({
                [classes.map]: heatmapType === 'NDVI' || heatmapType === 'LAI',
              }),
            }}
            onClick={handleWmsLayerClick}
            onLoad={handleMapLoad}
          >
            {(heatmapType === 'LAI' || heatmapType === 'NDVI') &&
            maps.data.legend ? (
              <LegendControl>
                <GradientLegend legend={maps.data.legend} />
              </LegendControl>
            ) : null}
            {wmsInfoPopup}
            <WmsLayer
              maxbounds={maps.data.maxbounds ?? undefined}
              url={selectedMap.url}
            />
            <PlotsLayer
              data={{ type: 'FeatureCollection', features: [field.data] }}
              outline
            />
          </PfMap>
          <SelectImageSlider
            key={heatmapType + maps.data?.options?.length}
            onChange={setSelectedHeatmap}
            options={maps.data?.options || []}
            value={selectedHeatmap || ''}
          />
        </Stack>
      );
    }
    return null;
  };

  const renderLoadingState = () => {
    if (!maps.data || maps.data?.status === FORM_STATUS.PENDING) {
      return (
        <Center h="100%" p="xl">
          <Stack align="center">
            <Text fw="bold" size="xl" ta="center">
              {t('Nous sommes en train de préparer vos cartes')}
            </Text>
            <Loader />
          </Stack>
        </Center>
      );
    }
    return null;
  };

  return (
    <FieldMapsLayout
      content={
        <>
          {renderMapContent()}
          {renderLoadingState()}
        </>
      }
      header={
        <Group h="100%" justify="space-between" p="md">
          <Group>
            {type !== 'single' && (
              <SelectMapType
                maps={field.data.properties.maps}
                onChange={handleCompareChange}
                value={type === 'compare-left' ? compare?.[0] : compare?.[1]}
              />
            )}
            <Select
              data={layersOptions(t)}
              disabled={maps.data?.status !== FORM_STATUS.FINISHED}
              onChange={(v) => setHeatmapType(v ?? 'LAI')}
              value={heatmapType}
            />
          </Group>
        </Group>
      }
    />
  );
}

export default SatelliteMap;
